import { isEqual } from 'vet-bones/utils';

import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';

/**
 * Detects if the model has changed by comparing it to the draft data.
 * The draft data represents the state before the refresh operation.
 *
 * Returns true if any changes detected in:
 * - Nodes and links (classes, hierarchies, relationships)
 * - Attributes (datatype properties)
 * - Rules
 * - Sensitive property groups
 * - Labels, descriptions, colors, or geo data
 */
export const hasModelChangedFromDraft = (
  draftData: NonNullable<ModelData['draftData']>,
  model: Model
): boolean => {
  // Compare node and link data
  if (!isEqual(draftData.prevNodeDataArray, model.nodeDataArray)) {
    return true;
  }
  if (!isEqual(draftData.prevLinkDataArray, model.linkDataArray)) {
    return true;
  }

  // Compare model metadata
  const draftModelData = draftData.prevModelData;
  const currentModelData = model.modelData;

  // Check attributes
  if (
    !isEqual(draftModelData.attributesByIri, currentModelData.attributesByIri)
  ) {
    return true;
  }

  // Check rules by comparing top-level fields. Rules from the database are always
  // text-type, so we compare name/text/type/models. We intentionally skip ifClause
  // and thenClause since their variable IDs are regenerated each time the rule text
  // is parsed by the srs() parser in parseModelRuleResults.
  const draftRuleIds = Object.keys(draftModelData.rulesById);
  const currentRuleIds = Object.keys(currentModelData.rulesById);
  if (!isEqual(draftRuleIds.sort(), currentRuleIds.sort())) {
    return true;
  }
  const hasRuleChanged = draftRuleIds.some((ruleId) => {
    const draftRule = draftModelData.rulesById[ruleId];
    const currentRule = currentModelData.rulesById[ruleId];
    return (
      draftRule.name !== currentRule.name ||
      draftRule.text !== currentRule.text ||
      draftRule.type !== currentRule.type ||
      !isEqual(draftRule.models, currentRule.models)
    );
  });
  if (hasRuleChanged) {
    return true;
  }

  // Check sensitive property groups
  if (
    !isEqual(
      draftModelData.sensitivePropertyGroupsByName,
      currentModelData.sensitivePropertyGroupsByName
    )
  ) {
    return true;
  }

  // Check metadata (labels, descriptions, colors, geo data)
  if (!isEqual(draftModelData.labelsByIri, currentModelData.labelsByIri)) {
    return true;
  }
  if (
    !isEqual(
      draftModelData.descriptionsByIri,
      currentModelData.descriptionsByIri
    )
  ) {
    return true;
  }
  if (!isEqual(draftModelData.colorIdsByIri, currentModelData.colorIdsByIri)) {
    return true;
  }
  if (!isEqual(draftModelData.geoDataByIri, currentModelData.geoDataByIri)) {
    return true;
  }

  // No changes detected
  return false;
};
